import styled from "styled-components";

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 32px 24px;
  background-color: #f9fcff;
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

export const TabsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
`;

export const Tab = styled.button<{ active?: boolean }>`
  padding: 12px 24px;
  border: none;
  border-radius: 8px 8px 0 0;
  background-color: ${(props) => (props.active ? "#fff" : "#e0e0e0")};
  color: ${(props) => (props.active ? "#1976d2" : "#666")};
  font-weight: ${(props) => (props.active ? "600" : "400")};
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: ${(props) => (props.active ? "2px solid #1976d2" : "none")};

  &:hover {
    background-color: ${(props) => (props.active ? "#fff" : "#d0d0d0")};
  }
`;

export const ContentCard = styled.div`
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0px 4px 18px 0px rgba(75, 70, 92, 0.1);
  padding: 24px;
  flex: 1;
  overflow: auto;
`;

export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

export const InfoCard = styled.div`
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const InfoLabel = styled.span`
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
  font-weight: 500;
`;

export const InfoValue = styled.span<{ color?: string }>`
  font-size: 18px;
  font-weight: 600;
  color: ${(props) => props.color || "#333"};
`;

export const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 24px 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
`;

export const ProjectsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;

  th,
  td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #eee;
  }

  th {
    background-color: #f5f5f5;
    font-weight: 600;
    color: #333;
    font-size: 12px;
    text-transform: uppercase;
  }

  td {
    font-size: 14px;
    color: #666;
  }

  tr:hover {
    background-color: #f9f9f9;
  }
`;

export const StatusBadge = styled.span<{ variant: "success" | "warning" | "error" | "info" }>`
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  
  ${(props) => {
    switch (props.variant) {
      case "success":
        return `
          background-color: #e8f5e9;
          color: #2e7d32;
        `;
      case "warning":
        return `
          background-color: #fff3e0;
          color: #ef6c00;
        `;
      case "error":
        return `
          background-color: #ffebee;
          color: #c62828;
        `;
      case "info":
      default:
        return `
          background-color: #e3f2fd;
          color: #1565c0;
        `;
    }
  }}
`;

export const ButtonsContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
`;

export const ActionButton = styled.button<{ variant?: "primary" | "secondary" | "danger" }>`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;

  ${(props) => {
    switch (props.variant) {
      case "danger":
        return `
          background-color: #d32f2f;
          color: #fff;
          &:hover {
            background-color: #b71c1c;
          }
          &:disabled {
            background-color: #ef9a9a;
            cursor: not-allowed;
          }
        `;
      case "secondary":
        return `
          background-color: #f5f5f5;
          color: #333;
          border: 1px solid #ddd;
          &:hover {
            background-color: #e0e0e0;
          }
          &:disabled {
            background-color: #fafafa;
            color: #bbb;
            cursor: not-allowed;
          }
        `;
      case "primary":
      default:
        return `
          background-color: #1976d2;
          color: #fff;
          &:hover {
            background-color: #1565c0;
          }
          &:disabled {
            background-color: #90caf9;
            cursor: not-allowed;
          }
        `;
    }
  }}
`;

export const LogsContainer = styled.div`
  background-color: #1e1e1e;
  border-radius: 8px;
  padding: 16px;
  max-height: 300px;
  overflow-y: auto;
  font-family: "Fira Code", "Monaco", "Consolas", monospace;
  font-size: 13px;
  line-height: 1.5;
`;

export const LogEntry = styled.div<{ type?: "info" | "success" | "error" | "warning" }>`
  padding: 4px 0;
  color: ${(props) => {
    switch (props.type) {
      case "success":
        return "#4caf50";
      case "error":
        return "#f44336";
      case "warning":
        return "#ff9800";
      case "info":
      default:
        return "#64b5f6";
    }
  }};
`;

export const LogTimestamp = styled.span`
  color: #888;
  margin-right: 8px;
`;

export const SearchInput = styled.input`
  padding: 10px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  width: 300px;

  &:focus {
    outline: none;
    border-color: #1976d2;
    box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.1);
  }
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  color: #999;
  text-align: center;
`;

export const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;
